import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, Typography,
  FormControl, Select, MenuItem, Switch, FormControlLabel, CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import SellerMultiSelect from './shared/SellerMultiSelect';
import OptionMultiSelect from './shared/OptionMultiSelect';
import CategoryMultiSelect from './shared/CategoryMultiSelect';
import { useProductFilterMaster } from './shared/productFilterApiHelpers';
import LocationCascadeSelect from './shared/LocationCascadeSelect';
import { resolveCityLabelsForSingleRule, slaRuleGeoResolutionKey } from './shared/slaDisplayResolvers';
import { normalizeSlaRule } from './shared/helpers';
import { buildSlaRulePayload, EMPTY_SLA_RULE_FORM } from './AttributeRuleForm';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: '100%',
    '& .MuiOutlinedInput-root': { backgroundColor: '#f5f5f5' },
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  multiSelect: {
    backgroundColor: '#f5f5f5',
  },
}));

const ruleToForm = (rule) => {
  const n = normalizeSlaRule(rule);
  return {
    rule_name: n.rule_name,
    last_mile_warehouse: n.last_mile_warehouse,
    destination_countries: [...(n.destination_countries || [])],
    destination_cities: [...(n.destination_cities || [])],
    category_ids: [...(n.category_ids || [])],
    sold_by: [...(n.sold_by || [])],
    seller_ids: [...(n.seller_ids || [])],
    brand_names: [...(n.brand_names || [])],
    additional_sla: String(n.additional_sla ?? ''),
    priority: String(n.priority ?? 0),
    status: n.status,
  };
};

const SlaRuleEditDialog = ({
  open,
  rule,
  onClose,
  onSave,
  saving,
  warehouses,
  countries,
  getCountryName,
}) => {
  const classes = useStyles();
  const [form, setForm] = useState({ ...EMPTY_SLA_RULE_FORM });
  const [prefetchedCityLabels, setPrefetchedCityLabels] = useState({});
  const {
    soldBy: soldByOptions,
    brandName: brandOptions,
    category: categoryTree,
    loading: filterLoading,
    error: filterError,
  } = useProductFilterMaster();

  useEffect(() => {
    if (open && rule) {
      setForm(ruleToForm(rule));
    }
  }, [open, rule]);

  const ruleCityPrefetchKey = useMemo(() => slaRuleGeoResolutionKey(rule), [rule]);

  useEffect(() => {
    if (!open || !rule) {
      setPrefetchedCityLabels({});
      return undefined;
    }
    let cancelled = false;
    resolveCityLabelsForSingleRule(rule, { getCountryName })
      .then((m) => {
        if (!cancelled) setPrefetchedCityLabels(m || {});
      })
      .catch(() => {
        if (!cancelled) setPrefetchedCityLabels({});
      });
    return () => { cancelled = true; };
  }, [open, ruleCityPrefetchKey, getCountryName]);

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const countryOptions = useMemo(
    () => (countries || []).map((c) => ({ id: c.code, label: c.name })),
    [countries]
  );

  const handleSave = async () => {
    if (!rule) return;
    const id = rule._id || rule.id;
    if (!id || !form.rule_name.trim() || !form.last_mile_warehouse) return;
    const payload = buildSlaRulePayload(form, { omitEmptyArrays: false });
    await onSave(id, payload);
  };

  const canSave =
    Boolean(rule) &&
    form.rule_name.trim() &&
    form.last_mile_warehouse &&
    form.additional_sla !== '' &&
    Number(form.additional_sla) !== 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit SLA rule</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.formLabel}>Rule name *</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              value={form.rule_name}
              onChange={(e) => update('rule_name', e.target.value)}
              className={classes.formControl}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography className={classes.formLabel}>Last mile warehouse *</Typography>
            <FormControl variant="outlined" size="small" className={classes.formControl}>
              <Select
                value={form.last_mile_warehouse}
                onChange={(e) => update('last_mile_warehouse', e.target.value)}
                className={classes.multiSelect}
              >
                {warehouses.map((wh) => (
                  <MenuItem key={wh.warehouse_id} value={wh.warehouse_id}>
                    {wh.warehouse_name || wh.warehouse_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <OptionMultiSelect
              label="Destination countries"
              placeholder="Search and select countries"
              options={countryOptions}
              values={form.destination_countries}
              onChange={(next) => update('destination_countries', next)}
              emptyText="No countries found"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={<Switch checked={form.status} onChange={(e) => update('status', e.target.checked)} color="primary" />}
              label="Active"
            />
          </Grid>
          <Grid item xs={12}>
            <LocationCascadeSelect
              selectedCountries={form.destination_countries}
              warehouse={form.last_mile_warehouse}
              selectedCities={form.destination_cities}
              onCitiesChange={(ids) => update('destination_cities', ids)}
              getCountryName={getCountryName}
              prefetchedCityLabels={prefetchedCityLabels}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CategoryMultiSelect
              label="Categories"
              placeholder="Search and select categories"
              tree={categoryTree}
              values={form.category_ids}
              onChange={(next) => update('category_ids', next)}
              loading={filterLoading}
              error={filterError}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <OptionMultiSelect
              label="Sold by"
              placeholder="Search and select sold-by values"
              options={soldByOptions}
              values={form.sold_by}
              onChange={(next) => update('sold_by', next)}
              loading={filterLoading}
              error={filterError}
              emptyText="No sold-by values found"
              loadingText="Loading sold-by values..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <OptionMultiSelect
              label="Brand names"
              placeholder="Search and select brands"
              options={brandOptions}
              values={form.brand_names}
              onChange={(next) => update('brand_names', next)}
              loading={filterLoading}
              error={filterError}
              emptyText="No brands found"
              loadingText="Loading brands..."
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SellerMultiSelect
              label="Sellers"
              placeholder="Search and select sellers"
              values={form.seller_ids}
              onChange={(next) => update('seller_ids', next)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography className={classes.formLabel}>Additional SLA (days) *</Typography>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              value={form.additional_sla}
              onChange={(e) => update('additional_sla', e.target.value)}
              className={classes.formControl}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography className={classes.formLabel}>Priority</Typography>
            <TextField
              variant="outlined"
              size="small"
              type="number"
              value={form.priority}
              onChange={(e) => update('priority', e.target.value)}
              className={classes.formControl}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">Cancel</Button>
        <Button onClick={handleSave} color="primary" variant="contained" disabled={saving || !canSave}>
          {saving ? <CircularProgress size={22} /> : 'Save changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SlaRuleEditDialog;
